import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { List, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';

interface TocItem {
  id: string;
  text: string;
}

interface BlogTableOfContentsProps {
  contentHtml: string;
}

function extractHeadings(html: string): TocItem[] {
  const regex = /<h2[^>]*id="([^"]*)"[^>]*>(.*?)<\/h2>/gi;
  const items: TocItem[] = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    items.push({
      id: match[1],
      text: match[2].replace(/<[^>]*>/g, '').trim(),
    });
  }
  return items;
}

export function BlogTableOfContents({ contentHtml }: BlogTableOfContentsProps) {
  const headings = extractHeadings(contentHtml);
  const [activeId, setActiveId] = useState<string>('');
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const visible = entries.filter((e) => e.isIntersecting);
    if (visible.length > 0) {
      setActiveId(visible[0].target.id);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0.1,
    });
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings, handleIntersect]);

  if (headings.length < 2) return null;

  const linkList = (
    <nav className="space-y-1">
      {headings.map((h) => (
        <a
          key={h.id}
          href={`#${h.id}`}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (isMobile) setOpen(false);
          }}
          className={cn(
            'block text-sm py-1.5 px-3 rounded-md transition-colors leading-snug',
            activeId === h.id
              ? 'text-primary font-semibold bg-accent'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          )}
        >
          {h.text}
        </a>
      ))}
    </nav>
  );

  if (isMobile) {
    return (
      <Collapsible open={open} onOpenChange={setOpen} className="mb-6 border border-border rounded-xl bg-card">
        <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground">
          <span className="flex items-center gap-2">
            <List className="h-4 w-4" />
            Table of Contents
          </span>
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-2 pb-3">
          {linkList}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <aside>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-3">
        Contents
      </p>
      {linkList}
    </aside>
  );
}
